import React, { useEffect, useMemo, useRef, useState } from "react";

import { dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";

import { useOperationDespatchContext } from "../../../../../../contexts/DespatchContext";
import { ResourceIcons, ResourceIconsProps } from "../ResourceList/components/ResourceIcons";
import { getCurrentSlotConfig, analyzerSlotSourceId } from "../Viewers/useDragSystem/slotTypes";
import { ResourceSlotSubItem } from "./ResourceSlotSubItem";
import {
  Container,
  ContainerItem,
  ContainerIcon,
  PlaceholderContainer,
  PlaceholderContent,
  DroppableAreaContainer,
} from "./styled";

interface ResourceSlotProps {
  id: string;
  title?: string | React.ReactNode | React.ReactNode[];
  children?: React.ReactNode;
  icon?: React.ReactNode;
  iconName?: ResourceIconsProps["name"];
  placeHolder?: string;
  wrapPlaceHolder?: boolean;
  active?: boolean;
  disableGutters?: boolean;
  renderAtRight?: React.ReactNode;
  isDropDisabled?: boolean;
  isDropPlaceHolderDisabled?: boolean;
  backgroundColor?: string;
  backgroundColorActive?: string;
}

export const ResourceSlot = (props: ResourceSlotProps) => {
  const {
    id,
    active,
    children,
    placeHolder,
    title,
    icon,
    iconName,
    disableGutters,
    renderAtRight,
    wrapPlaceHolder = true,
    isDropDisabled,
    backgroundColor,
    backgroundColorActive,
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [isUsingPlaceholder, setIsUsingPlaceholder] = useState(false);

  const { currentDraggable, selectedOption, draggedItemHeight } = useOperationDespatchContext();

  const currentSlotConfig = useMemo(() => {
    const { name, type } = analyzerSlotSourceId(id);

    if (!name || type !== "slot") return;

    return getCurrentSlotConfig(selectedOption, name);
  }, [id]);

  const canDrop = useMemo(() => {
    if (!currentSlotConfig || !currentDraggable?.isValid || currentDraggable?.type !== "source")
      return false;

    return currentSlotConfig.validSources.includes(currentDraggable?.name) && !isDropDisabled;
  }, [currentSlotConfig, currentDraggable, isDropDisabled]);

  const shouldHighlight = useMemo(() => {
    if (
      !currentSlotConfig ||
      !currentDraggable?.isValid ||
      currentDraggable?.type !== "source" ||
      isDropDisabled
    ) {
      return false;
    }

    return currentSlotConfig.validSources.includes(currentDraggable?.name);
  }, [currentSlotConfig, currentDraggable, isDropDisabled]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    return dropTargetForElements({
      element,
      canDrop: ({ source }) => {
        // use source.data to analyze the draggable
        const draggedPayload = analyzerSlotSourceId(source.data.sourceId as string);
        if (
          !currentSlotConfig ||
          !draggedPayload.isValid ||
          draggedPayload.type !== "source" ||
          isDropDisabled
        ) {
          return false;
        }
        return currentSlotConfig.validSources.includes(draggedPayload.name);
      },
      getData: () => ({
        slotId: id,
        slotConfig: currentSlotConfig,
      }),
      onDragEnter: () => {
        setIsDraggingOver(true);
        setIsUsingPlaceholder(true);
      },
      onDragLeave: () => {
        setIsDraggingOver(false);
        setIsUsingPlaceholder(false);
      },
      onDrop: () => {
        setIsDraggingOver(false);
        setIsUsingPlaceholder(false);
      },
    });
  }, [id, currentSlotConfig, isDropDisabled]);

  const renderIcon = () => {
    if (!icon && !iconName) return;

    return (
      <ContainerIcon active={active}>
        {icon}
        {iconName && <ResourceIcons name={iconName} size={20} />}
      </ContainerIcon>
    );
  };

  const renderContent = () => {
    if (active) return title;

    if (wrapPlaceHolder)
      return <ResourceSlotSubItem disableHouver>{placeHolder}</ResourceSlotSubItem>;

    return placeHolder;
  };

  return (
    <DroppableAreaContainer
      ref={containerRef}
      isDraggingOver={(canDrop && isDraggingOver) || shouldHighlight}
      isDraggingLeader={currentDraggable?.name?.toLowerCase().includes("leader")}
    >
      <Container
        isDraggingOver={(canDrop && isDraggingOver) || shouldHighlight}
        backgroundColor={backgroundColor}
        backgroundColorActive={backgroundColorActive}
      >
        {renderIcon()}

        <ContainerItem active={active} disableGutters={disableGutters}>
          {renderContent()}
          {children}
          <PlaceholderContainer
            canDrop={canDrop}
            isUsingPlaceholder={isUsingPlaceholder}
            draggedItemHeight={draggedItemHeight}
          >
            {isUsingPlaceholder && canDrop && (
              <ResourceSlotSubItem forceHouver>
                <PlaceholderContent draggedItemHeight={draggedItemHeight} />
              </ResourceSlotSubItem>
            )}
          </PlaceholderContainer>
        </ContainerItem>

        {renderAtRight}
      </Container>
    </DroppableAreaContainer>
  );
};
